import { useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Pill,
  Syringe,
  Stethoscope,
  FlaskConical,
  Send,
  ClipboardList,
  Activity,
  RefreshCw,
  IdCard,
  Calendar,
  Building2,
  MapPin,
  Phone,
  Mail,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import useTitanApp from "@/hooks/useTitanApp";
import {
  addPatientAllergies,
  addPatientMedications,
  getPatientAllergies,
  getPatientMedications,
} from "@/dispatchers/patientDispatchers";
import {
  GpConnectRecord,
  emptyGpConnectRecord,
  formatGpDate,
} from "./GpConnectRecordModel";

interface GpConnectRecordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  record?: GpConnectRecord;
  loading?: boolean;
  onSyncMedications?: (ids: string[]) => void;
  onSyncAllergies?: (ids: string[]) => void;
  onSyncObservations?: (ids: string[]) => void;
}

type SyncCategory = "medications" | "allergies" | "observations";

function SectionTitle({
  icon: Icon,
  title,
  count,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  count: number;
}) {
  return (
    <span className="flex items-center gap-2">
      <Icon className="w-4 h-4 text-[#005EB8]" />
      <span className="text-sm font-semibold">{title}</span>
      <span className="text-xs text-muted-foreground">({count})</span>
    </span>
  );
}

export function GpConnectRecordDialog({
  open,
  onOpenChange,
  record = emptyGpConnectRecord,
  loading = false,
  onSyncMedications,
  onSyncAllergies,
  onSyncObservations,
}: GpConnectRecordDialogProps) {
  const { toast } = useToast();
  const { patientState } = useTitanApp();
  const { selectedPatientDetails, consultationId } = patientState || {};
  const patientId =
    selectedPatientDetails?.patientID || selectedPatientDetails?.patientId || "";
  const [selectedMeds, setSelectedMeds] = useState<Set<string>>(new Set());
  const [selectedAllergies, setSelectedAllergies] = useState<Set<string>>(
    new Set(),
  );
  const [selectedObs, setSelectedObs] = useState<Set<string>>(new Set());
  const [syncedMeds, setSyncedMeds] = useState<Set<string>>(new Set());
  const [syncedAllergies, setSyncedAllergies] = useState<Set<string>>(
    new Set(),
  );
  const [syncedObs, setSyncedObs] = useState<Set<string>>(new Set());
  const [syncAllOpen, setSyncAllOpen] = useState(false);
  const [syncAllCats, setSyncAllCats] = useState<Set<SyncCategory>>(
    new Set(["medications", "allergies", "observations"]),
  );

  const patientName = useMemo(
    () =>
      [
        record.patient.title,
        record.patient.forename,
        record.patient.middleName,
        record.patient.surname,
      ]
        .filter(Boolean)
        .join(" "),
    [record.patient],
  );

  const hasAnyRecordData = useMemo(() => {
    return (
      record.medications.length > 0 ||
      record.allergies.length > 0 ||
      record.immunisations.length > 0 ||
      record.problems.length > 0 ||
      record.consultations.length > 0 ||
      record.observations.length > 0 ||
      record.referrals.length > 0 ||
      record.uncategorisedData.length > 0
    );
  }, [record]);

  const toggle = (
    set: Set<string>,
    setter: (s: Set<string>) => void,
    id: string,
  ) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setter(next);
  };

  const mapSeverityToEnum = (severity?: string | null): number => {
    const value = (severity || "").toLowerCase().trim();
    if (value === "severe") return 3;
    if (value === "moderate") return 2;
    return 1;
  };

  const getMedicationTherapyLevel = (med: any): number => {
    const parsed = Number(med?.therapyLevel);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
    return 1;
  };

  const syncMeds = async () => {
    const ids = [...selectedMeds];
    if (ids.length === 0) return;

    if (!patientId) {
      toast({
        title: "Unable to sync",
        description: "Patient ID is missing.",
        variant: "destructive",
      });
      return;
    }

    const selectedMedsPayload = record.medications
      .filter((m) => ids.includes(m.medicationId))
      .map((m) => ({
        itemName: m.drugName || "",
        dose: m.dosage || m.dosageInstruction || "",
        quantity: m.quantity?.toString?.() || "0",
        therapyLevel: getMedicationTherapyLevel(m),
        additionalNotes: m.dosageInstruction || "",
      }));

    if (selectedMedsPayload.length > 0) {
      await addPatientMedications({
        consultationId: consultationId || "",
        patientId,
        medications: selectedMedsPayload,
      });
      await getPatientMedications(patientId);
    }

    setSyncedMeds(new Set([...syncedMeds, ...ids]));
    setSelectedMeds(new Set());
    onSyncMedications?.(ids);
    toast({
      title: "Medications synced",
      description: `${ids.length} medication${ids.length > 1 ? "s" : ""} imported into TITANVerse.`,
    });
  };

  const syncAllergies = async () => {
    const ids = [...selectedAllergies];
    if (ids.length === 0) return;

    if (!patientId) {
      toast({
        title: "Unable to sync",
        description: "Patient ID is missing.",
        variant: "destructive",
      });
      return;
    }

    const selectedAllergiesPayload = record.allergies
      .filter((a) => ids.includes(a.allergyId))
      .map((a) => ({
        allergyName: a.substanceName || "",
        severity: mapSeverityToEnum(a.severity),
        typicalReaction: a.manifestationDescription || "",
        additionalNotes: a.note || "",
      }));

    if (selectedAllergiesPayload.length > 0) {
      await addPatientAllergies({
          consultationId: consultationId || "",
        patientId,
        allergies: selectedAllergiesPayload,
      });
      await getPatientAllergies(patientId);
    }

    setSyncedAllergies(new Set([...syncedAllergies, ...ids]));
    setSelectedAllergies(new Set());
    onSyncAllergies?.(ids);
    toast({
      title: "Allergies synced",
      description: `${ids.length} allerg${ids.length > 1 ? "ies" : "y"} imported into TITANVerse.`,
    });
  };

  const syncObs = () => {
    const ids = [...selectedObs];
    if (ids.length === 0) return;
    setSyncedObs(new Set([...syncedObs, ...ids]));
    setSelectedObs(new Set());
    onSyncObservations?.(ids);
    toast({
      title: "Observations synced",
      description: `${ids.length} observation${ids.length > 1 ? "s" : ""} imported into TITANVerse.`,
    });
  };

  const toggleSyncCat = (cat: SyncCategory) => {
    const next = new Set(syncAllCats);
    if (next.has(cat)) next.delete(cat);
    else next.add(cat);
    setSyncAllCats(next);
  };

  const syncAll = async () => {
    if ((syncAllCats.has("medications") || syncAllCats.has("allergies")) && !patientId) {
      toast({
        title: "Unable to sync",
        description: "Patient ID is missing.",
        variant: "destructive",
      });
      return;
    }

    let total = 0;
    if (syncAllCats.has("medications")) {
      const ids = record.medications.map((m) => m.medicationId);

      for (const m of record.medications) {
        const payload = {
          itemName: m.drugName || "",
          dose: m.dosage || m.dosageInstruction || "",
          quantity: m.quantity?.toString?.() || "0",
          therapyLevel: getMedicationTherapyLevel(m),
          additionalNotes: m.dosageInstruction || "",
        };

        await addPatientMedications({
          consultationId: consultationId || "",
          patientId,
          medications: [payload],
        });
      }

      await getPatientMedications(patientId);

      setSyncedMeds(new Set(ids));
      setSelectedMeds(new Set());
      onSyncMedications?.(ids);
      total += ids.length;
    }
    if (syncAllCats.has("allergies")) {
      const ids = record.allergies.map((a) => a.allergyId);

      for (const a of record.allergies) {
        const payload = {
          allergyName: a.substanceName || "",
          severity: mapSeverityToEnum(a.severity),
          typicalReaction: a.manifestationDescription || "",
          additionalNotes: a.note || "",
        };

        await addPatientAllergies({
          consultationId: consultationId || "",
          patientId,
          allergies: [payload],
        });
      }

      await getPatientAllergies(patientId);

      setSyncedAllergies(new Set(ids));
      setSelectedAllergies(new Set());
      onSyncAllergies?.(ids);
      total += ids.length;
    }
    if (syncAllCats.has("observations")) {
      const ids = record.observations.map((o) => o.observationId);
      setSyncedObs(new Set(ids));
      setSelectedObs(new Set());
      onSyncObservations?.(ids);
      total += ids.length;
    }
    setSyncAllOpen(false);
    if (total === 0) return;
    toast({
      title: "Records synced",
      description: `${total} item${total > 1 ? "s" : ""} imported into TITANVerse.`,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0 gap-0 overflow-hidden flex flex-col">
        <DialogHeader className="px-6 py-4 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <span className="inline-flex items-center rounded-sm bg-[#005EB8] px-1.5 py-0.5 text-[11px] font-bold italic leading-none tracking-tight text-white">
              NHS
            </span>
            GP Connect — Full GP Record
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto w-full">
          <div className="px-6 py-4 w-full">
            {/* Patient banner */}
            <div className="rounded-lg border bg-white p-4 mb-2">
              {/* Header row: name + sync */}
              <div className="flex items-start justify-between gap-3 pb-3 mb-3 border-b">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <h3 className="text-base font-semibold text-gray-900">
                    {patientName || "Patient"}
                  </h3>
                  <Badge variant="secondary">{record.patient.sex}</Badge>
                </div>
                <Popover open={syncAllOpen} onOpenChange={setSyncAllOpen}>
                  <PopoverTrigger asChild>
                    <Button size="sm">
                      <RefreshCw />
                      Sync All
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-64">
                    <p className="text-sm font-medium">Choose what to sync</p>
                    <p className="text-xs text-muted-foreground mb-3">
                      All items in the selected categories will be imported.
                    </p>
                    <div className="space-y-2">
                      {(
                        [
                          { id: "medications", label: "Medications" },
                          { id: "allergies", label: "Allergies" },
                          { id: "observations", label: "Observations" },
                        ] as { id: SyncCategory; label: string }[]
                      ).map((cat) => (
                        <label
                          key={cat.id}
                          className="flex items-center gap-2 text-sm cursor-pointer"
                        >
                          <Checkbox
                            checked={syncAllCats.has(cat.id)}
                            onCheckedChange={() => toggleSyncCat(cat.id)}
                          />
                          {cat.label}
                        </label>
                      ))}
                    </div>
                    <Button
                      size="sm"
                      className="w-full mt-4"
                      onClick={syncAll}
                      disabled={syncAllCats.size === 0}
                    >
                      <RefreshCw />
                      Sync selected
                    </Button>
                  </PopoverContent>
                </Popover>
              </div>

              {loading && (
                <div className="rounded-md border border-blue-100 bg-blue-50 px-3 py-2 mb-3 text-sm text-blue-700">
                  Fetching GP Connect record...
                </div>
              )}

              {!loading && !hasAnyRecordData && (
                <div className="rounded-md border border-amber-100 bg-amber-50 px-3 py-2 mb-3 text-sm text-amber-700">
                  No GP Connect record data found for this patient.
                </div>
              )}

              {/* Detail grid: icon + label/value */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-3">
                {(
                  [
                    {
                      icon: IdCard,
                      label: "NHS No.",
                      value: record.patient.nhsNumber,
                    },
                    {
                      icon: Calendar,
                      label: "Date of birth",
                      value: formatGpDate(record.patient.dob),
                    },
                    {
                      icon: Building2,
                      label: "Surgery",
                      value: record.patient.surgery,
                    },
                    {
                      icon: Phone,
                      label: "Phone",
                      value: record.patient.phone,
                    },
                    {
                      icon: Mail,
                      label: "Email",
                      value: record.patient.email,
                      className: "col-span-2",
                    },
                    {
                      icon: MapPin,
                      label: "Address",
                      value: record.patient.address,
                      className: "col-span-2 md:col-span-3",
                    },
                  ] as {
                    icon: React.ComponentType<{ className?: string }>;
                    label: string;
                    value: string;
                    className?: string;
                  }[]
                ).map((item) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.label}
                      className={`flex items-start gap-2 min-w-0 ${item.className ?? ""}`}
                    >
                      <Icon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-gray-400" />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-gray-500">
                          {item.label}
                        </div>
                        <div className="text-sm font-medium leading-tight text-gray-900 break-words">
                          {item.value}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <Accordion type="multiple">
              {/* Medications (syncable) */}
              <AccordionItem value="medications">
                <AccordionTrigger>
                  <SectionTitle
                    icon={Pill}
                    title="Medications"
                    count={record.medications.length}
                  />
                </AccordionTrigger>
                <AccordionContent className="space-y-2">
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      onClick={syncMeds}
                      disabled={selectedMeds.size === 0}
                    >
                      <Send />
                      Sync to TITANVerse
                      {selectedMeds.size > 0 ? ` (${selectedMeds.size})` : ""}
                    </Button>
                  </div>
                  {record.medications.map((med) => {
                    const isSynced = syncedMeds.has(med.medicationId);
                    return (
                      <div
                        key={med.medicationId}
                        className="flex items-start gap-3 rounded-lg border p-3"
                      >
                        {isSynced ? (
                          <CheckCircle2 className="w-4 h-4 mt-0.5 text-green-600 shrink-0" />
                        ) : (
                          <Checkbox
                            className="mt-0.5"
                            checked={selectedMeds.has(med.medicationId)}
                            onCheckedChange={() =>
                              toggle(
                                selectedMeds,
                                setSelectedMeds,
                                med.medicationId,
                              )
                            }
                            aria-label={`Select ${med.drugName}`}
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-medium">
                              {med.drugName}
                            </span>
                            <Badge
                              variant={
                                med.status === "active"
                                  ? "default"
                                  : "secondary"
                              }
                              className="capitalize"
                            >
                              {med.status}
                            </Badge>
                            <Badge variant="outline">
                              {med.prescriptionType}
                            </Badge>
                            {isSynced && (
                              <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                                Synced
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {med.dosage} · {med.dosageInstruction} · Qty{" "}
                            {med.quantity}
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            Last issued {formatGpDate(med.lastIssuedDate)} ·
                            Review {formatGpDate(med.reviewDate)} ·{" "}
                            {med.prescribedByName}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </AccordionContent>
              </AccordionItem>

              {/* Allergies (syncable) */}
              <AccordionItem value="allergies">
                <AccordionTrigger>
                  <SectionTitle
                    icon={AlertTriangle}
                    title="Allergies & Adverse Reactions"
                    count={record.allergies.length}
                  />
                </AccordionTrigger>
                <AccordionContent className="space-y-2">
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      onClick={syncAllergies}
                      disabled={selectedAllergies.size === 0}
                    >
                      <Send />
                      Sync to TITANVerse
                      {selectedAllergies.size > 0
                        ? ` (${selectedAllergies.size})`
                        : ""}
                    </Button>
                  </div>
                  {record.allergies.map((allergy) => {
                    const isSynced = syncedAllergies.has(allergy.allergyId);
                    return (
                      <div
                        key={allergy.allergyId}
                        className="flex items-start gap-3 rounded-lg border p-3"
                      >
                        {isSynced ? (
                          <CheckCircle2 className="w-4 h-4 mt-0.5 text-green-600 shrink-0" />
                        ) : (
                          <Checkbox
                            className="mt-0.5"
                            checked={selectedAllergies.has(allergy.allergyId)}
                            onCheckedChange={() =>
                              toggle(
                                selectedAllergies,
                                setSelectedAllergies,
                                allergy.allergyId,
                              )
                            }
                            aria-label={`Select ${allergy.substanceName}`}
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-medium">
                              {allergy.substanceName}
                            </span>
                            <Badge
                              variant={
                                allergy.criticality === "high"
                                  ? "destructive"
                                  : "secondary"
                              }
                              className="capitalize"
                            >
                              {allergy.criticality} risk
                            </Badge>
                            <Badge variant="outline" className="capitalize">
                              {allergy.verificationStatus}
                            </Badge>
                            {isSynced && (
                              <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                                Synced
                              </Badge>
                            )}
                          </div>
                          {(allergy.manifestationDescription || allergy.severity || allergy.category) && (
                            <div className="text-xs text-muted-foreground mt-1">
                              {allergy.manifestationDescription && (
                                <span>{allergy.manifestationDescription}</span>
                              )}
                              {allergy.manifestationDescription && allergy.severity && <span> · </span>}
                              {allergy.severity && (
                                <span className="capitalize">{allergy.severity}</span>
                              )}
                              {(allergy.manifestationDescription || allergy.severity) && allergy.category && <span> · </span>}
                              {allergy.category && <span>{allergy.category}</span>}
                            </div>
                          )}
                          {allergy.note && (
                            <div className="text-xs text-muted-foreground mt-0.5">
                              {allergy.note}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </AccordionContent>
              </AccordionItem>

              {/* Problems */}
              <AccordionItem value="problems">
                <AccordionTrigger>
                  <SectionTitle
                    icon={ClipboardList}
                    title="Problems & Diagnoses"
                    count={record.problems.length}
                  />
                </AccordionTrigger>
                <AccordionContent className="space-y-2">
                  {record.problems.map((problem) => (
                    <div
                      key={problem.problemId}
                      className="rounded-lg border p-3"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium">
                          {problem.problemName}
                        </span>
                        <Badge
                          variant={
                            problem.clinicalStatus === "active"
                              ? "default"
                              : "secondary"
                          }
                          className="capitalize"
                        >
                          {problem.clinicalStatus}
                        </Badge>
                        <Badge variant="outline" className="capitalize">
                          {problem.significance}
                        </Badge>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Onset {formatGpDate(problem.onsetDate)}
                        {problem.abatementDate
                          ? ` · Resolved ${formatGpDate(problem.abatementDate)}`
                          : ""}{" "}
                        · {problem.recordedBy}
                      </div>
                      {problem.note && (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {problem.note}
                        </div>
                      )}
                    </div>
                  ))}
                </AccordionContent>
              </AccordionItem>

              {/* Observations */}
              <AccordionItem value="observations">
                <AccordionTrigger>
                  <SectionTitle
                    icon={Activity}
                    title="Observations & Results"
                    count={record.observations.length}
                  />
                </AccordionTrigger>
                <AccordionContent className="space-y-2">
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      onClick={syncObs}
                      disabled={selectedObs.size === 0}
                    >
                      <Send />
                      Sync to TITANVerse
                      {selectedObs.size > 0 ? ` (${selectedObs.size})` : ""}
                    </Button>
                  </div>
                  {record.observations.map((obs) => {
                    const isSynced = syncedObs.has(obs.observationId);
                    return (
                      <div
                        key={obs.observationId}
                        className="flex items-center gap-3 rounded-lg border p-3"
                      >
                        {isSynced ? (
                          <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
                        ) : (
                          <Checkbox
                            checked={selectedObs.has(obs.observationId)}
                            onCheckedChange={() =>
                              toggle(
                                selectedObs,
                                setSelectedObs,
                                obs.observationId,
                              )
                            }
                            aria-label={`Select ${obs.observationName}`}
                          />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">
                              {obs.observationName}
                            </span>
                            {isSynced && (
                              <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                                Synced
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            {formatGpDate(obs.effectiveDate)} ·{" "}
                            {obs.performerName}
                            {obs.referenceRangeLow || obs.referenceRangeHigh
                              ? ` · Ref ${obs.referenceRangeLow ?? "—"}–${obs.referenceRangeHigh ?? "—"}`
                              : ""}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-sm font-semibold">
                            {obs.valueString ??
                              `${obs.valueQuantity ?? "—"} ${obs.valueUnit ?? ""}`}
                          </div>
                          {obs.interpretation && (
                            <Badge
                              variant={
                                obs.interpretation === "Normal"
                                  ? "secondary"
                                  : "default"
                              }
                              className="mt-1"
                            >
                              {obs.interpretation}
                            </Badge>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </AccordionContent>
              </AccordionItem>

              {/* Immunisations */}
              <AccordionItem value="immunisations">
                <AccordionTrigger>
                  <SectionTitle
                    icon={Syringe}
                    title="Immunisations"
                    count={record.immunisations.length}
                  />
                </AccordionTrigger>
                <AccordionContent className="space-y-2">
                  {record.immunisations.map((imm) => (
                    <div
                      key={imm.immunisationId}
                      className="rounded-lg border p-3"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium">
                          {imm.vaccineName}
                        </span>
                        {imm.doseNumber != null && (
                          <Badge variant="outline">Dose {imm.doseNumber}</Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {formatGpDate(imm.administrationDate)} · {imm.site} ·{" "}
                        {imm.route} · Batch {imm.batchNumber} ·{" "}
                        {imm.administeredByName}
                      </div>
                    </div>
                  ))}
                </AccordionContent>
              </AccordionItem>

              {/* Consultations */}
              <AccordionItem value="consultations">
                <AccordionTrigger>
                  <SectionTitle
                    icon={Stethoscope}
                    title="Consultations"
                    count={record.consultations.length}
                  />
                </AccordionTrigger>
                <AccordionContent className="space-y-2">
                  {record.consultations.map((enc) => (
                    <div
                      key={enc.consultationId}
                      className="rounded-lg border p-3"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-sm font-medium">
                          {enc.consultationTypeDisplay}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {formatGpDate(enc.consultationDate)}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {enc.practitionerName} · {enc.organisationName}
                      </div>
                      <div className="mt-2 space-y-2">
                        {enc.topics.map((topic) => (
                          <div
                            key={topic.topicId}
                            className="rounded-md bg-muted/40 p-2"
                          >
                            <div className="text-xs font-medium">
                              {topic.title}
                            </div>
                            <ul className="mt-1 space-y-0.5">
                              {topic.entries.map((entry) => (
                                <li
                                  key={entry.entryId}
                                  className="text-xs text-muted-foreground"
                                >
                                  <span className="font-medium text-foreground">
                                    {entry.entryType}:
                                  </span>{" "}
                                  {entry.display}
                                  {entry.value ? ` — ${entry.value}` : ""}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </AccordionContent>
              </AccordionItem>

              {/* Referrals */}
              <AccordionItem value="referrals">
                <AccordionTrigger>
                  <SectionTitle
                    icon={Send}
                    title="Referrals"
                    count={record.referrals.length}
                  />
                </AccordionTrigger>
                <AccordionContent className="space-y-2">
                  {record.referrals.map((ref) => (
                    <div key={ref.referralId} className="rounded-lg border p-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium">
                          {ref.referralDescription}
                        </span>
                        <Badge
                          variant={
                            ref.status === "active" ? "default" : "secondary"
                          }
                          className="capitalize"
                        >
                          {ref.status}
                        </Badge>
                        <Badge variant="outline" className="capitalize">
                          {ref.priority}
                        </Badge>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {ref.recipientOrganisationName} ·{" "}
                        {ref.recipientSpecialty} · Authored{" "}
                        {formatGpDate(ref.authoredDate)} · {ref.referrerName}
                      </div>
                      {ref.note && (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {ref.note}
                        </div>
                      )}
                    </div>
                  ))}
                </AccordionContent>
              </AccordionItem>

              {/* Lifestyle & Other */}
              <AccordionItem value="lifestyle" className="border-b-0">
                <AccordionTrigger>
                  <SectionTitle
                    icon={FlaskConical}
                    title="Lifestyle & Other"
                    count={record.uncategorisedData.length}
                  />
                </AccordionTrigger>
                <AccordionContent className="space-y-2">
                  {record.uncategorisedData.map((entry) => (
                    <div
                      key={entry.entryId}
                      className="flex items-center justify-between gap-3 rounded-lg border p-3"
                    >
                      <div className="text-sm font-medium">{entry.display}</div>
                      <div className="text-xs text-muted-foreground text-right">
                        {entry.value}
                        <div>{formatGpDate(entry.recordedDate)}</div>
                      </div>
                    </div>
                  ))}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
